import { cropSolutions } from '../data/crops';
import type { CropSolution, Product } from '../types';
import { canonicalProductSlug, productSlugCandidates } from './slug';

/**
 * The product <-> crop edges of the site's graph, resolved in one place.
 *
 * Two directions, and both used to be broken. A product page listed its crops as inert text, so
 * nothing connected AGN to the grape programme that documents its dose; and a crop page resolved its
 * recommended products by exact slug, so a renamed product vanished from it without a trace. Both are
 * real links now, and `npm run check:seo` fails the build if either side stops resolving.
 */

/** Every name a crop page answers to: its own name plus the names its dose tables use. */
export function cropNames(crop: CropSolution): string[] {
  return [...new Set([crop.name, ...(crop.aliases ?? [])])];
}

function norm(s: string): string {
  const n = String(s).toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  return n.endsWith('es') && n.length > 4 ? n.slice(0, -2) : n.endsWith('s') && n.length > 3 ? n.slice(0, -1) : n;
}

/** name/alias -> crop page slug, built once. */
const BY_NAME: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const c of cropSolutions) for (const n of cropNames(c)) m.set(norm(n), c.slug);
  return m;
})();

/**
 * The crop-programme path for one of a product's crop tags, or null when no programme page exists.
 * Broad tags the catalogue uses for breadth — "All crops", "Vegetables", "Fruits", "Turf" — have no
 * page and correctly return null: linking them to a specific crop's programme would misrepresent it.
 */
export function cropPagePath(cropTag: string): string | null {
  const slug = BY_NAME.get(norm(cropTag));
  return slug ? `/crop-solutions/${slug}` : null;
}

/** The crop-solution entry a tag names, if any. */
export function cropSolutionFor(cropTag: string): CropSolution | undefined {
  const slug = BY_NAME.get(norm(cropTag));
  return slug ? cropSolutions.find((c) => c.slug === slug) : undefined;
}

/** Resolve a crop programme's product slugs against a loaded catalogue, honouring renames. */
export function productsForCrop(crop: CropSolution, all: Product[]): Product[] {
  const bySlug = new Map(all.map((p) => [p.slug, p]));
  const out: Product[] = [];
  for (const wanted of crop.productSlugs) {
    const p = productSlugCandidates(wanted)
      .map((c) => bySlug.get(c))
      .find(Boolean);
    if (p && !out.includes(p)) out.push(p);
  }
  return out;
}

/** Crop programmes whose crop this product is documented for — the reverse edge. */
export function cropProgrammesForProduct(product: Product): CropSolution[] {
  const out: CropSolution[] = [];
  for (const tag of product.recommendedCrops ?? []) {
    const c = cropSolutionFor(tag);
    if (c && !out.includes(c)) out.push(c);
  }
  // A programme that names this product is a programme for it, even when the product's own crop tags
  // are broad ("All crops") and mention no single crop.
  for (const c of cropSolutions) {
    if (out.includes(c)) continue;
    if (c.productSlugs.some((s) => productSlugCandidates(s).includes(product.slug))) out.push(c);
  }
  return out;
}
